/**
 * PurchaseCelebration — a lightweight canvas-based celebration effect
 * specifically for subscription purchase success. Renders a short burst
 * of colorful particles over a light background, then fades out.
 *
 * Unlike CanvaStyleFireworks (dark sky, gold-only, infinite loop), this:
 *  - Works over light/white backgrounds (no dark overlay)
 *  - Uses ParkPro brand colors (blue, teal, indigo, gold)
 *  - Fires a controlled burst then auto-stops after ~4 seconds
 *  - Lightweight: no rockets/streaks/shake, just particle fountains
 */

import React, { useEffect, useRef, useCallback } from 'react';

const BRAND_PALETTE = [
  '#3b82f6', // blue-500
  '#14b8a6', // teal-500
  '#6366f1', // indigo-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#10b981', // emerald-500
  '#ec4899', // pink-500
  '#C9A227', // ParkPro gold
];

const TAU = Math.PI * 2;
const rand = (a, b) => a + Math.random() * (b - a);

class Particle {
  constructor(x, y, color) {
    const angle = Math.random() * TAU;
    const speed = rand(2, 8);
    this.x = x;
    this.y = y;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed - rand(2, 5);
    this.color = color;
    this.life = rand(1.2, 2.8);
    this.age = 0;
    this.size = rand(3, 7);
    this.rotation = rand(0, TAU);
    this.rotSpeed = rand(-4, 4);
    this.shape = Math.random() > 0.4 ? 'rect' : 'circle';
    this.gravity = rand(0.08, 0.14);
    this.drag = rand(0.97, 0.99);
  }

  step(dt) {
    this.age += dt;
    this.vx *= this.drag;
    this.vy *= this.drag;
    this.vy += this.gravity;
    this.x += this.vx;
    this.y += this.vy;
    this.rotation += this.rotSpeed * dt;
    return this.age < this.life;
  }

  draw(ctx) {
    const progress = this.age / this.life;
    const alpha = progress < 0.1
      ? progress / 0.1
      : 1 - Math.pow((progress - 0.1) / 0.9, 2);

    ctx.save();
    ctx.globalAlpha = Math.max(0, alpha * 0.85);
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);

    ctx.fillStyle = this.color;
    const s = this.size * (1 - progress * 0.3);

    if (this.shape === 'circle') {
      ctx.beginPath();
      ctx.arc(0, 0, s / 2, 0, TAU);
      ctx.fill();
    } else {
      ctx.fillRect(-s / 2, -s * 0.7, s, s * 1.4);
    }

    ctx.restore();
  }
}

const PurchaseCelebration = ({ duration = 4000 }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const startTimeRef = useRef(null);
  const fadingRef = useRef(false);

  const burst = useCallback((cx, cy, count = 40) => {
    for (let i = 0; i < count; i++) {
      const color = BRAND_PALETTE[Math.floor(Math.random() * BRAND_PALETTE.length)];
      particlesRef.current.push(new Particle(cx, cy, color));
    }
  }, []);

  const animate = useCallback((now) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!startTimeRef.current) startTimeRef.current = now;
    const elapsed = now - startTimeRef.current;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    // Fade the entire canvas as we approach the end
    if (elapsed > duration - 1000) {
      fadingRef.current = true;
      const fadeProgress = (elapsed - (duration - 1000)) / 1000;
      ctx.globalAlpha = Math.max(0, 1 - fadeProgress);
    }

    // Update & draw particles
    const dt = 1 / 60;
    for (let i = particlesRef.current.length - 1; i >= 0; i--) {
      const p = particlesRef.current[i];
      if (!p.step(dt)) {
        particlesRef.current.splice(i, 1);
      } else {
        p.draw(ctx);
      }
    }

    if (elapsed < duration) {
      animRef.current = requestAnimationFrame(animate);
    }
  }, [duration]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';

    // Staggered bursts from multiple origins
    const origins = [
      { x: W * 0.5, y: H * 0.35 },   // center
      { x: W * 0.25, y: H * 0.25 },   // top-left
      { x: W * 0.75, y: H * 0.25 },   // top-right
      { x: W * 0.35, y: H * 0.5 },    // mid-left
      { x: W * 0.65, y: H * 0.5 },    // mid-right
    ];

    // Initial burst
    burst(origins[0].x, origins[0].y, 50);

    // Staggered bursts
    const timers = [];
    timers.push(setTimeout(() => { burst(origins[1].x, origins[1].y, 30); }, 200));
    timers.push(setTimeout(() => { burst(origins[2].x, origins[2].y, 30); }, 350));
    timers.push(setTimeout(() => { burst(origins[3].x, origins[3].y, 25); }, 600));
    timers.push(setTimeout(() => { burst(origins[4].x, origins[4].y, 25); }, 800));
    // Second wave
    timers.push(setTimeout(() => { burst(origins[0].x, origins[0].y, 35); }, 1200));
    timers.push(setTimeout(() => { burst(origins[1].x, origins[1].y, 20); }, 1600));
    timers.push(setTimeout(() => { burst(origins[2].x, origins[2].y, 20); }, 1800));

    animRef.current = requestAnimationFrame(animate);

    return () => {
      timers.forEach(clearTimeout);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [animate, burst]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 50,
      }}
      aria-hidden="true"
    />
  );
};

export default PurchaseCelebration;
